import { z } from 'zod';
import {
  INMUEBLE_RED_MARKETPLACE_SITE,
  VIVIENDA_SOCIAL_MARKETPLACE_SITE,
} from './publicMarketplaceSites';
import {
  realEstateUnitAmenityKeySchema,
  realEstateUnitTypologySchema,
  unitTypeSchema,
} from './publicMarketplaceValidation';

export const PUBLIC_SUPPORT_SEMANTICS_VERSION = 'public-conversation-v1';
export const PUBLIC_SUPPORT_SEMANTICS_HEADER =
  'X-Inmueble-Public-Conversation-Version';

export enum PublisherSupportConversationContractVersion {
  V4 = 4,
}

export const PUBLISHER_SUPPORT_CONVERSATION_CONTRACT_VERSION =
  PublisherSupportConversationContractVersion.V4;

const LOCATION_QUERY_MAX_LENGTH = 120;
const LOCATION_PATH_SLUG_MAX_LENGTH = 500;
const LOCATION_DISPLAY_LABEL_MAX_LENGTH = 100;
const LOCATION_CLARIFICATION_LIMIT = 5;
const SUMMARY_MAX_LENGTH = 4000;
const SUMMARY_QUESTION_MAX_LENGTH = 1000;
const LOCALE_MAX_LENGTH = 35;
const LISTING_REFERENCE_LIMIT = 10;
const AGENCY_SLUG_MAX_LENGTH = 200;
const CAPABILITY_BUILD_SHA_MAX_LENGTH = 128;
const SEARCH_FILTER_LIMIT = 20;
const AMENITY_FILTER_LIMIT = 50;

export const CRM_SUPPORT_SITE = Object.freeze({
  id: 'crm_inmueble_red' as const,
  name: 'CRM Inmueble.red',
  canonicalOrigin: 'https://crm.inmueble.red',
});

export const PUBLISHER_SUPPORT_SITE_ORIGINS = Object.freeze({
  inmueble_red: INMUEBLE_RED_MARKETPLACE_SITE.canonicalOrigin,
  vivienda_social: VIVIENDA_SOCIAL_MARKETPLACE_SITE.canonicalOrigin,
  crm_inmueble_red: CRM_SUPPORT_SITE.canonicalOrigin,
  punta_del_sur: 'https://puntadelsur.pe',
});

export const publisherSupportAudienceSchema = z.enum([
  'unknown',
  'publisher_prospect',
  'linked_publisher',
  'anonymous_home_seeker',
  'linked_home_seeker',
]);

export const publisherSupportConversationDomainSchema = z.enum([
  'publisher',
  'home_search',
  'site_support',
]);

export const publisherSupportConversationTransitionSchema = z.enum([
  'continue',
  'start_new',
  'switch',
  'interrupt',
]);

export const publisherSupportSiteIdSchema = z.enum([
  'inmueble_red',
  'vivienda_social',
  'crm_inmueble_red',
  'punta_del_sur',
]);

export const publisherSupportActiveTaskSchema = z.enum([
  'publisher_onboarding',
  'listing_claim',
  'publisher_profile',
  'property_search',
  'listing_question',
  'inquiry_follow_up',
  'account_link',
  'site_support',
  'human_handoff',
]);

export const publisherSupportIntentSchema = z.enum([
  'cost',
  'tokko_crm',
  'activate',
  'manual',
  'claim_link',
  'profile_trust',
  'site_trust_support',
  'property_search',
  'property_detail',
  'similar_listings',
  'agency_profile',
  'saved_searches',
  'saved_listings',
  'buyer_inquiries',
  'publisher_listings',
  'publisher_inquiries',
  'publisher_profile',
  'account_help',
  'financing',
  'viewing',
  'complaint',
  'greeting',
  'unknown',
  'opt_out',
]);

export const publisherSupportUnitTypeSchema = unitTypeSchema;

export const publisherSupportUnitTypologySchema = realEstateUnitTypologySchema;

export const publisherSupportHomeSearchCriteriaSchema = z
  .object({
    transaction: z.enum(['buy', 'rent']).nullable().default(null),
    locationQuery: z
      .string()
      .max(LOCATION_QUERY_MAX_LENGTH)
      .nullable()
      .default(null),
    locationPathSlug: z
      .string()
      .max(LOCATION_PATH_SLUG_MAX_LENGTH)
      .nullable()
      .default(null),
    locationDisplayLabel: z
      .string()
      .max(LOCATION_DISPLAY_LABEL_MAX_LENGTH)
      .nullable()
      .default(null),
    unitTypes: z
      .array(publisherSupportUnitTypeSchema)
      .max(SEARCH_FILTER_LIMIT)
      .default([]),
    unitTypologies: z
      .array(publisherSupportUnitTypologySchema)
      .max(SEARCH_FILTER_LIMIT)
      .default([]),
    bedroomsMinimum: z.number().int().nonnegative().nullable().default(null),
    bedroomsMaximum: z.number().int().nonnegative().nullable().default(null),
    priceCurrency: z.enum(['USD', 'PEN']).nullable().default(null),
    priceMinimumMajor: z.string().regex(/^\d+$/u).nullable().default(null),
    priceMaximumMajor: z.string().regex(/^\d+$/u).nullable().default(null),
    bathroomsMinimum: z.number().int().nonnegative().nullable().default(null),
    bathroomsMaximum: z.number().int().nonnegative().nullable().default(null),
    areaSquareMetersMinimum: z
      .number()
      .int()
      .nonnegative()
      .nullable()
      .default(null),
    areaSquareMetersMaximum: z
      .number()
      .int()
      .nonnegative()
      .nullable()
      .default(null),
    parkingSpacesMinimum: z
      .number()
      .int()
      .nonnegative()
      .nullable()
      .default(null),
    parkingSpacesMaximum: z
      .number()
      .int()
      .nonnegative()
      .nullable()
      .default(null),
    sortBy: z
      .enum(['newest', 'price_ascending', 'price_descending'])
      .nullable()
      .default(null),
    excludeOwnListings: z.boolean().default(false),
    amenityKeys: z
      .array(realEstateUnitAmenityKeySchema)
      .max(AMENITY_FILTER_LIMIT)
      .default([]),
  })
  .strict();

export const publisherSupportLocationClarificationSchema = z
  .object({
    locationQuery: z.string().min(1).max(LOCATION_PATH_SLUG_MAX_LENGTH),
    candidateDisplayLabels: z
      .array(z.string().min(1).max(LOCATION_DISPLAY_LABEL_MAX_LENGTH))
      .min(2)
      .max(LOCATION_CLARIFICATION_LIMIT),
  })
  .strict();

const publisherSupportDomainStateSchema = z
  .object({
    lastIntent: publisherSupportIntentSchema.nullable().default(null),
    unresolvedQuestion: z
      .string()
      .max(SUMMARY_QUESTION_MAX_LENGTH)
      .nullable()
      .default(null),
  })
  .strict();

export const publisherSupportHomeSearchStateSchema = z
  .object({
    criteria: publisherSupportHomeSearchCriteriaSchema,
    lastIntent: publisherSupportIntentSchema.nullable().default(null),
    locationClarification: publisherSupportLocationClarificationSchema
      .nullable()
      .default(null),
    unresolvedQuestion: z
      .string()
      .max(SUMMARY_QUESTION_MAX_LENGTH)
      .nullable()
      .default(null),
    referencedListingPublicSlugs: z
      .array(z.string().min(1).max(LOCATION_PATH_SLUG_MAX_LENGTH))
      .max(LISTING_REFERENCE_LIMIT)
      .default([]),
  })
  .strict();

export const publisherSupportConversationSummarySchema = z
  .object({
    narrative: z.string().max(SUMMARY_MAX_LENGTH),
    activeDomain: publisherSupportConversationDomainSchema.nullable(),
    publisher: publisherSupportDomainStateSchema,
    homeSearch: publisherSupportHomeSearchStateSchema,
    siteSupport: publisherSupportDomainStateSchema,
    referencedAgencySlug: z
      .string()
      .min(1)
      .max(AGENCY_SLUG_MAX_LENGTH)
      .nullable()
      .default(null),
    supportSiteId: publisherSupportSiteIdSchema.nullable().default(null),
  })
  .strict();

export const publisherSupportConversationContextUpdateSchema = z
  .object({
    contractVersion: z.literal(PUBLISHER_SUPPORT_CONVERSATION_CONTRACT_VERSION),
    expectedConversationVersion: z.number().int().nonnegative(),
    audience: publisherSupportAudienceSchema.optional(),
    locale: z
      .string()
      .trim()
      .min(1)
      .max(LOCALE_MAX_LENGTH)
      .nullable()
      .optional(),
    activeTask: publisherSupportActiveTaskSchema.nullable().optional(),
    conversationSummary: publisherSupportConversationSummarySchema
      .nullable()
      .optional(),
    processedInboundMessageId: z.string().uuid().nullable().optional(),
    deliveredSummaryThroughMessageId: z.string().uuid().nullable().optional(),
  })
  .strict();

export const publisherSupportConversationCapabilitySchema = z
  .object({
    contractVersion: z.number().int().positive(),
    buildSha: z.string().min(1).max(CAPABILITY_BUILD_SHA_MAX_LENGTH),
    publicConversationVersion: z.literal(PUBLIC_SUPPORT_SEMANTICS_VERSION),
    inboundAuthorityReady: z.literal(true),
    operatorApprovalReady: z.literal(true),
  })
  .strict();

export type PublisherSupportAudienceContract = z.infer<
  typeof publisherSupportAudienceSchema
>;
export type PublisherSupportConversationDomain = z.infer<
  typeof publisherSupportConversationDomainSchema
>;
export type PublisherSupportConversationTransition = z.infer<
  typeof publisherSupportConversationTransitionSchema
>;
export type PublisherSupportSiteId = z.infer<
  typeof publisherSupportSiteIdSchema
>;
export type PublisherSupportActiveTaskContract = z.infer<
  typeof publisherSupportActiveTaskSchema
>;
export type PublisherSupportIntentContract = z.infer<
  typeof publisherSupportIntentSchema
>;
export type PublisherSupportUnitType = z.infer<
  typeof publisherSupportUnitTypeSchema
>;
export type PublisherSupportUnitTypology = z.infer<
  typeof publisherSupportUnitTypologySchema
>;
export type PublisherSupportHomeSearchCriteria = z.infer<
  typeof publisherSupportHomeSearchCriteriaSchema
>;
export type PublisherSupportLocationClarificationContract = z.infer<
  typeof publisherSupportLocationClarificationSchema
>;
export type PublisherSupportConversationSummaryContract = z.infer<
  typeof publisherSupportConversationSummarySchema
>;
export type PublisherSupportConversationContextUpdate = z.infer<
  typeof publisherSupportConversationContextUpdateSchema
>;
export type PublisherSupportConversationCapability = z.infer<
  typeof publisherSupportConversationCapabilitySchema
>;

export interface PublisherSupportConversationContextCandidate {
  conversationSummary: PublisherSupportConversationSummaryContract | null;
  processedInboundMessageId: string | null;
  deliveredSummaryThroughMessageId: string | null;
  updatedAt: string;
}

export interface PublisherSupportMergedConversationContext {
  conversationSummary: PublisherSupportConversationSummaryContract | null;
  processedInboundMessageId: string | null;
  deliveredSummaryThroughMessageId: string | null;
  sourceIndex: number | null;
}

export const emptyPublisherSupportHomeSearchCriteria =
  (): PublisherSupportHomeSearchCriteria =>
    publisherSupportHomeSearchCriteriaSchema.parse({});

export const emptyPublisherSupportConversationSummary =
  (): PublisherSupportConversationSummaryContract =>
    publisherSupportConversationSummarySchema.parse({
      narrative: '',
      activeDomain: null,
      publisher: {},
      homeSearch: { criteria: {}, lastIntent: null },
      siteSupport: {},
      referencedAgencySlug: null,
      supportSiteId: null,
    });

const compareConversationContextCandidates = (
  left: PublisherSupportConversationContextCandidate,
  right: PublisherSupportConversationContextCandidate
): number => {
  const leftCursor =
    left.processedInboundMessageId ?? left.deliveredSummaryThroughMessageId;
  const rightCursor =
    right.processedInboundMessageId ?? right.deliveredSummaryThroughMessageId;
  if (leftCursor && rightCursor) {
    const cursorOrder = leftCursor.localeCompare(rightCursor);
    if (cursorOrder !== 0) return cursorOrder;
  } else if (leftCursor) {
    return 1;
  } else if (rightCursor) {
    return -1;
  }
  return left.updatedAt.localeCompare(right.updatedAt);
};

export const mergePublisherSupportConversationContexts = (
  candidates: PublisherSupportConversationContextCandidate[]
): PublisherSupportMergedConversationContext => {
  const summarized = candidates
    .map((candidate, sourceIndex) => ({ candidate, sourceIndex }))
    .filter(
      (
        entry
      ): entry is {
        candidate: PublisherSupportConversationContextCandidate & {
          conversationSummary: PublisherSupportConversationSummaryContract;
        };
        sourceIndex: number;
      } => entry.candidate.conversationSummary !== null
    )
    .sort((left, right) =>
      compareConversationContextCandidates(left.candidate, right.candidate)
    );
  const latest = summarized.at(-1);
  if (!latest) {
    return {
      conversationSummary: null,
      processedInboundMessageId: null,
      deliveredSummaryThroughMessageId: null,
      sourceIndex: null,
    };
  }

  const narratives = summarized
    .map((entry) => entry.candidate.conversationSummary.narrative.trim())
    .filter(
      (narrative, index, all) =>
        narrative.length > 0 && all.indexOf(narrative) === index
    )
    .join('\n')
    .slice(-SUMMARY_MAX_LENGTH);
  const referencedListingPublicSlugs = [
    ...latest.candidate.conversationSummary.homeSearch
      .referencedListingPublicSlugs,
    ...summarized
      .slice(0, -1)
      .reverse()
      .flatMap(
        (entry) =>
          entry.candidate.conversationSummary.homeSearch
            .referencedListingPublicSlugs
      ),
  ].filter((slug, index, all) => all.indexOf(slug) === index);

  return {
    conversationSummary: {
      ...latest.candidate.conversationSummary,
      narrative: narratives,
      homeSearch: {
        ...latest.candidate.conversationSummary.homeSearch,
        referencedListingPublicSlugs: referencedListingPublicSlugs.slice(
          0,
          LISTING_REFERENCE_LIMIT
        ),
      },
      referencedAgencySlug:
        latest.candidate.conversationSummary.referencedAgencySlug ??
        summarized
          .slice(0, -1)
          .reverse()
          .find(
            (entry) =>
              entry.candidate.conversationSummary.referencedAgencySlug !== null
          )?.candidate.conversationSummary.referencedAgencySlug ??
        null,
    },
    processedInboundMessageId:
      latest.candidate.processedInboundMessageId ?? null,
    deliveredSummaryThroughMessageId:
      latest.candidate.deliveredSummaryThroughMessageId ?? null,
    sourceIndex: latest.sourceIndex,
  };
};

export const parsePublisherSupportHomeSearchCriteria = (
  value: unknown
): PublisherSupportHomeSearchCriteria => {
  const result = publisherSupportHomeSearchCriteriaSchema.safeParse(value);
  if (!result.success) throw result.error;
  return result.data;
};

export const parsePublisherSupportConversationSummary = (
  value: unknown
): PublisherSupportConversationSummaryContract => {
  const result = publisherSupportConversationSummarySchema.safeParse(value);
  if (!result.success) throw result.error;
  return result.data;
};

export const parsePublisherSupportConversationContextUpdate = (
  value: unknown
): PublisherSupportConversationContextUpdate => {
  const result =
    publisherSupportConversationContextUpdateSchema.safeParse(value);
  if (!result.success) throw result.error;
  return result.data;
};
